import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "i18n-fe";
import useLastReconcile from "../hooks/useLastReconcile";

// #3349 (stuck-sheet-audit workstream) -- the persisted outcome of the show's
// last FVD reconcile. A show whose reconcile is blocked (cross-org gate) or
// failing silently stops updating from FVD; before this the only trace was a
// CloudWatch log line. Healthy shows get one quiet line; anything else gets an
// Alert naming what happened, and every state carries the "Re-run sync" action.
//
// #3630 -- the dashboard shows the outcome as plain text in its header line
// (ReconcileSyncText) and, when something is wrong, an Alert that links to the
// Re-run sync control (ReconcileSyncAlert). Re-run sync itself lives on the
// Settings page (the default export below, in the FVD sync card).
const MAX_ROWS_SHOWN = 5;

// The Settings card the dashboard's Alert links to (Settings scrolls to a
// `#<card id>` hash, ADR 0004: a link to the exact place).
export const FVD_SYNC_CARD_ID = "fvd-sync-card";
export const FVD_SYNC_SETTINGS_PATH = `/settings#${FVD_SYNC_CARD_ID}`;

function shortWhen(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString([], {
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// Static keys per status (i18n keys must be greppable, never built).
function problemCopy(outcome, t, when) {
  return (
    {
      blocked: {
        title: t("dashboard.reconcile.blockedTitle"),
        body: t("dashboard.reconcile.blockedBody", { when }),
      },
      partial: {
        title: t("dashboard.reconcile.partialTitle", { count: outcome.failedCount ?? 0 }),
        body: t("dashboard.reconcile.partialBody", { when }),
      },
      error: {
        title: t("dashboard.reconcile.errorTitle"),
        body: t("dashboard.reconcile.errorBody", { when }),
      },
    }[outcome.status] ?? {
      title: t("dashboard.reconcile.errorTitle"),
      body: t("dashboard.reconcile.errorBody", { when }),
    }
  );
}

function FailedRows({ outcome, t }) {
  const failedRows = outcome.failedRows ?? [];
  const shown = failedRows.slice(0, MAX_ROWS_SHOWN);
  const hidden = (outcome.failedCount ?? failedRows.length) - shown.length;
  return (
    <>
      {shown.map((row) => (
        <Typography key={`${row.entity}:${row.id}`} variant="caption" display="block">
          {t("dashboard.reconcile.failedRow", {
            entity: row.entity,
            id: row.id,
            error: row.error,
          })}
        </Typography>
      ))}
      {hidden > 0 && (
        <Typography variant="caption" display="block">
          {t("dashboard.reconcile.moreRows", { count: hidden })}
        </Typography>
      )}
    </>
  );
}

// #3630 -- the dashboard header's sync status: one quiet line of text, never a
// button. `reconcile` is the useLastReconcile result, read once by the page.
export function ReconcileSyncText({ reconcile }) {
  const { t } = useTranslation("scoreadmin");
  const { outcome, loading } = reconcile;
  if (loading) return null;
  const problem = outcome && outcome.status !== "ok";
  let text;
  if (!outcome) text = t("dashboard.reconcile.headerNever");
  else if (problem) text = t("dashboard.reconcile.headerProblem");
  else text = t("dashboard.reconcile.headerOk", { when: shortWhen(outcome.at) });
  return (
    <Typography
      variant="body2"
      color={problem ? "error.main" : "text.secondary"}
      data-testid="reconcile-status"
      sx={{ whiteSpace: "nowrap" }}
    >
      {outcome && !problem ? "\u2713 " : ""}
      {text}
    </Typography>
  );
}

// #3630 -- a blocked / partial / failing sync keeps its Alert on the dashboard,
// says what is wrong and why, and links to the Re-run sync control on Settings.
export function ReconcileSyncAlert({ reconcile }) {
  const { t } = useTranslation("scoreadmin");
  const { outcome, loading } = reconcile;
  if (loading || !outcome || outcome.status === "ok") return null;
  const when = outcome.at ? new Date(outcome.at).toLocaleString() : "";
  const copy = problemCopy(outcome, t, when);
  return (
    <Alert
      severity={outcome.status === "partial" ? "warning" : "error"}
      action={
        <Button
          size="small"
          color="inherit"
          component={RouterLink}
          to={FVD_SYNC_SETTINGS_PATH}
          sx={{ whiteSpace: "nowrap" }}
          data-testid="reconcile-open-settings"
        >
          {t("dashboard.reconcile.openSettings")}
        </Button>
      }
      data-testid={`reconcile-alert-${outcome.status}`}
    >
      <Typography variant="subtitle2">{copy.title}</Typography>
      <Typography variant="body2">{copy.body}</Typography>
      <FailedRows outcome={outcome} t={t} />
    </Alert>
  );
}

function RerunButton({ onClick, running, t }) {
  return (
    <Button
      size="small"
      variant="outlined"
      sx={{ whiteSpace: "nowrap" }}
      onClick={onClick}
      disabled={running}
      data-testid="reconcile-rerun"
    >
      {running ? t("dashboard.reconcile.rerunning") : t("dashboard.reconcile.rerun")}
    </Button>
  );
}

// The Settings page's FVD sync section: the full status (a quiet line, or the
// Alert) with the Re-run sync action.
export default function ReconcileStatusSection({ showId }) {
  const { t } = useTranslation("scoreadmin");
  const { outcome, loading, rerunning, rerunError, runReconcile } = useLastReconcile(showId);

  if (loading) return null;

  const when = outcome?.at ? new Date(outcome.at).toLocaleString() : "";
  const rerun = <RerunButton onClick={runReconcile} running={rerunning} t={t} />;

  let body;
  if (!outcome) {
    body = (
      <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
        <Typography variant="body2" color="text.secondary">
          {t("dashboard.reconcile.never")}
        </Typography>
        {rerun}
      </Stack>
    );
  } else if (outcome.status === "ok") {
    body = (
      <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
        <Typography variant="body2" color="text.secondary">
          {t("dashboard.reconcile.okSummary", { when })}
        </Typography>
        {rerun}
      </Stack>
    );
  } else {
    const copy = problemCopy(outcome, t, when);
    body = (
      <Alert
        severity={outcome.status === "partial" ? "warning" : "error"}
        action={rerun}
        data-testid={`reconcile-alert-${outcome.status}`}
      >
        <Typography variant="subtitle2">{copy.title}</Typography>
        <Typography variant="body2">{copy.body}</Typography>
        <FailedRows outcome={outcome} t={t} />
      </Alert>
    );
  }

  return (
    <Box data-testid="reconcile-status">
      {body}
      {rerunError && (
        <Alert severity="error" sx={{ mt: 1 }} data-testid="reconcile-rerun-error">
          {t("dashboard.reconcile.rerunFailed")}
        </Alert>
      )}
    </Box>
  );
}
